import { useEffect, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../authentication/AuthContext";
import Layout from "../shared/Layout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { logAnalyticsEvent } from "../engagement/Amplitude";
import MDEditor from "@uiw/react-md-editor";
import {
  useShowTempErrorMessage,
  useShowTempMessage,
} from "../shared/hooks/useUtilityContext";
import GetPromptButton from "./components/GetPromptButton";
import { getGeminiEmbedding } from "../ai/GeminiUtils";
import { useDraftTask } from "./hooks/useDraftTask";

const EditDraftTask = () => {
  const { id } = useParams();

  const { data: task, isLoading, isError, error } = useDraftTask(parseInt(id!));

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [textCount, setTextCount] = useState(0);
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description);
      setTextCount((task.description ?? "").length);
      setTags(task.tags);
    }
  }, [task]);

  const { user, setUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const showTempMessage = useShowTempMessage();
  const showTempErrorMessage = useShowTempErrorMessage();

  useEffect(() => {
    // Load form data from local storage when the component mounts
    const savedTitle = localStorage.getItem("taskTitle");
    const savedDescription = localStorage.getItem("taskDescription");
    const savedTags = localStorage.getItem("taskTags");

    if (savedTitle) {
      setTitle(savedTitle);
    }
    if (savedDescription) {
      setDescription(savedDescription);
    }
    if (savedTags) {
      setTags(JSON.parse(savedTags));
    }
  }, []);

  useEffect(() => {
    // Save form data to local storage whenever it changes
    localStorage.setItem("taskTitle", title);
    localStorage.setItem("taskDescription", description);
    localStorage.setItem("taskTags", JSON.stringify(tags));
  }, [title, description, tags]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (submitting) {
      return;
    }

    setSubmitting(true);

    logAnalyticsEvent("Press Submit Guide", {
      method: "draft",
    });
    const embedding = await getGeminiEmbedding(title + " " + description);
    const { data, error } = await supabase
      .from("tasks")
      .insert([
        {
          title,
          description,
          tags,
          user_id: user?.id,
          vote_total: 0,
          embedding: embedding,
        },
      ])
      .select("*");

    if (error) {
      logAnalyticsEvent("Error Submitting Guide", {
        method: "draft",
      });
      setSubmitting(false);
      console.error("Error submitting guide:", error);
    } else {
      await supabase.from("task_drafts").delete().eq("id", id);

      logAnalyticsEvent("Guide Submitted", {
        method: "draft",
      });
      setSubmitting(false);
      console.log("Guide submitted successfully:", data);
      // Reset form fields after successful submission
      setTitle("");
      setDescription("");
      setTags([]);
      localStorage.removeItem("taskTitle");
      localStorage.removeItem("taskDescription");
      localStorage.removeItem("taskTags");
      navigate(`/snippet/${data![0].id}`);
    }
  };

  const handleSaveDraft = async (e: any) => {
    e.preventDefault();

    if (submitting) {
      return;
    }

    setSubmitting(true);

    logAnalyticsEvent("Press Save Draft");
    const { data, error } = await supabase
      .from("task_drafts")
      .update([
        {
          title,
          description,
          tags,
          user_id: user?.id,
        },
      ])
      .eq("id", id)
      .select("*");

    if (error) {
      logAnalyticsEvent("Error Saving Draft Guide");
      setSubmitting(false);
      console.error("Error saving draft Guide:", error);
    } else {
      logAnalyticsEvent("Guide Draft Saved");
      setSubmitting(false);
      console.log("Guide Draft saved successfully:", data);
      // Reset form fields after successful submission
      setTitle("");
      setDescription("");
      setTags([]);
      localStorage.removeItem("taskTitle");
      localStorage.removeItem("taskDescription");
      localStorage.removeItem("taskTags");
      showTempMessage("Draft saved successfully");
      navigate(`/`);
    }
  };

  return (
    <Layout>
      {isLoading && <div className="loading loading-lg"></div>}
      {isError && (
        <div className="text-lg text-gray-500 text-center mt-8">
          Error loading task
        </div>
      )}
      {!isLoading && !isError && (
        <div className="container mx-auto py-8">
          <div className="flex gap-4 items-center">
            <h1 className="text-3xl font-bold mx-4">Create a New Guide</h1>
            <div className="badge badge-outline">Draft</div>
          </div>
          <form
            onSubmit={handleSubmit}
            className="mx-4 mt-4"
          >
            <div className="form-control mb-4">
              <span className="text-md font-bold">
                <span>Title</span>
              </span>
              <span className="prose text-sm font-light">
                Use the{" "}
                <a
                  href="https://en.wikipedia.org/wiki/Imperative_mood"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  imperative mood
                </a>{" "}
                to start the title. For example, "Create a new React app" or
                "Send a Slack Notification".
              </span>
              <input
                type="text"
                placeholder="Enter the guide title"
                className="input input-bordered mt-2"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
            <div className="flex justify-end w-full mb-4">
              <GetPromptButton title={title} />
            </div>
            <div className="form-control mb-4">
              <div className="text-md font-bold flex items-center">
                <div>Description</div>
              </div>

              <div className="text-sm font-light mb-2">
                {`Include all the information someone would need to complete the
              task.`}
              </div>
              <ul className="text-sm mb-4">
                <li>- Be concise, like you are programming a robot</li>
                <li>- Use Markdown</li>
                <li>- Each (#) Header will be a new step</li>
              </ul>
              <MDEditor
                //ref={descriptionRef}
                //placeholder="Enter the task steps"
                //rows={5}
                //maxLength={3000}
                height={"100%"}
                minHeight={300}
                visibleDragbar={false}
                maxHeight={1000}
                value={description}
                onChange={(e) => {
                  setDescription(e ?? "");
                  setTextCount((e ?? "").length);
                }}
              ></MDEditor>
              <div className="flex justify-end items-center mt-2">
                <progress
                  className="progress w-56 mr-4"
                  value={(textCount / 3000) * 100}
                  max="100"
                ></progress>
                <p className="text-sm text-end">{textCount}/3000</p>
              </div>
            </div>
            <div className="form-control mb-8">
              <span className="text-md font-bold">
                <span>Tags</span>
              </span>
              <label className="text-sm font-light">
                Tags help others find your guide. Separate tags with commas.
              </label>
              <input
                type="text"
                placeholder="Enter tags (comma-separated)"
                className="input input-bordered mt-2"
                value={tags}
                onChange={(e) =>
                  setTags(
                    e.target.value
                      .split(",")
                      .map((tag) => tag.toLowerCase().trim())
                  )
                }
              />
            </div>
            <div className="flex items-center gap-4">
              <button
                type="submit"
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                Submit
              </button>
              <button
                type="submit"
                className="btn btn-outline"
                onClick={handleSaveDraft}
              >
                Save Draft
              </button>
              {submitting && (
                <div className="loading loading-spinner ml-4"></div>
              )}
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
};

export default EditDraftTask;
